import { Globe } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { C } from '../lib/landingColors'
import {
  LANDING_LOCALES,
  setLandingLocaleOverride,
  useLandingLocale,
  useLandingRoute,
  type LandingLocale,
  type Multilingual,
} from '../lib/marketingLocale'

/** Endonyms — a visitor looking for their language recognises it in that language. */
const ENDONYM: Multilingual<string> = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  es: 'Español',
  pt: 'Português',
  hu: 'Magyar',
  nl: 'Nederlands',
  pl: 'Polski',
  cs: 'Čeština',
}

/** The control's accessible name, in the language currently shown. */
const ARIA_LABEL: Multilingual<string> = {
  en: 'Language',
  de: 'Sprache',
  fr: 'Langue',
  it: 'Lingua',
  es: 'Idioma',
  pt: 'Idioma',
  hu: 'Nyelv',
  nl: 'Taal',
  pl: 'Język',
  cs: 'Jazyk',
}

/**
 * Language selector for the landing page (ten languages since RB-43).
 *
 * A native `<select>` on purpose: it opens the platform picker (a full-width
 * wheel on iOS), which is the most reliable one-handed control on a phone and
 * costs no bundle weight. The visible face is the quiet globe + current code;
 * the endonym list lives in the platform dropdown.
 *
 * Selecting writes the landing-only override via `setLandingLocaleOverride` —
 * it only seeds the app UI language when nobody is signed in and no preference
 * is stored (W1, `seedUiLocaleFromLanding`); it never overwrites a member's, because this browser may also be
 * logged in as a studio member whose admin uiLocale is not marketing's to
 * touch (see `marketingLocale.ts`).
 *
 * On a market URL (`/hu`) the route fixes the language, so an override alone
 * would change nothing on screen. Choosing another language there records the
 * choice and moves the visitor to `/welcome`, the URL that speaks it.
 */
export function LandingLanguageSelect({ className = '' }: { className?: string }) {
  const locale = useLandingLocale()
  const marketRoute = useLandingRoute()
  const navigate = useNavigate()

  const choose = (next: LandingLocale) => {
    setLandingLocaleOverride(next)
    if (marketRoute && next !== marketRoute.locale) navigate('/welcome')
  }

  return (
    <label
      className={`relative inline-flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-sm font-medium transition-colors ${className}`}
      style={{ color: C.muted }}
      onMouseEnter={e => (e.currentTarget.style.color = C.text)}
      onMouseLeave={e => (e.currentTarget.style.color = C.muted)}
    >
      <Globe aria-hidden="true" className="w-4 h-4" />
      <span aria-hidden="true" className="uppercase tracking-wide text-xs font-semibold">{locale}</span>
      <select
        aria-label={ARIA_LABEL[locale]}
        value={locale}
        onChange={e => choose(e.target.value as LandingLocale)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      >
        {LANDING_LOCALES.map(l => (
          <option key={l} value={l} lang={l}>
            {ENDONYM[l]}
          </option>
        ))}
      </select>
    </label>
  )
}
